import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import { RemirrorManager, getLazyArray } from '@remirror/core';
import { DocExtension } from '@remirror/extension-doc';
import { EventsExtension } from '@remirror/extension-events';
import { GapCursorExtension } from '@remirror/extension-gap-cursor';
import { HistoryExtension } from '@remirror/extension-history';
import { ParagraphExtension } from '@remirror/extension-paragraph';
import { PositionerExtension } from '@remirror/extension-positioner';
import { TextExtension } from '@remirror/extension-text';

var _excluded = ["core"];
/**
 * The options for the core preset.
 */

var defaultOptions = _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, DocExtension.defaultOptions), ParagraphExtension.defaultOptions), HistoryExtension.defaultOptions), {}, {
  excludeExtensions: []
});
/**
 * The core preset is included by default when creating an editor with
 * `@remirror/react`.
 *
 * It comes with the the following extensions.
 *
 * - `HistoryExtension` - for undo and redo functionality
 * - `DocExtension` - provides the top level prosemirror node.
 * - `TextExtension` - provides the prosemirror text node
 * - `ParagraphExtension` - provides the prosemirror paragraph node
 * - `PositionerExtension` - set up automatic position checking and creation of
 *   virtual nodes from any part of the editor.
 */


function corePreset() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  options = _objectSpread(_objectSpread({}, defaultOptions), options);
  var _options = options,
      content = _options.content,
      depth = _options.depth,
      getDispatch = _options.getDispatch,
      getState = _options.getState,
      newGroupDelay = _options.newGroupDelay,
      excludeExtensions = _options.excludeExtensions;
  var excludeMap = {};

  var _iterator = _createForOfIteratorHelper(excludeExtensions !== null && excludeExtensions !== void 0 ? excludeExtensions : []),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var name = _step.value;
      excludeMap[name] = true;
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  var coreExtensions = [];

  if (!excludeMap['history']) {
    var historyExtension = new HistoryExtension({
      depth,
      getDispatch,
      getState,
      newGroupDelay
    });
    coreExtensions.push(historyExtension);
  }

  if (!excludeMap['doc']) {
    coreExtensions.push(new DocExtension({
      content
    }));
  }

  if (!excludeMap['text']) {
    coreExtensions.push(new TextExtension());
  }

  if (!excludeMap['paragraph']) {
    coreExtensions.push(new ParagraphExtension());
  }

  if (!excludeMap['positioner']) {
    coreExtensions.push(new PositionerExtension());
  }

  if (!excludeMap['gapCursor']) {
    coreExtensions.push(new GapCursorExtension());
  }

  if (!excludeMap['events']) {
    coreExtensions.push(new EventsExtension());
  }

  return coreExtensions;
}

/**
 * Create a manager with the core preset already applied.
 */
function createCoreManager(extensions) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  var core = options.core,
      managerSettings = _objectWithoutProperties(options, _excluded);

  return RemirrorManager.create(() => [...getLazyArray(extensions), ...corePreset(core)], managerSettings);
}

export { corePreset, createCoreManager };
